// components/EmailTemplates/StatsCards.js
import React from "react";
import {
    Card,
    CardBody,
    Grid,
    Icon,
    Spinner,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    useColorModeValue,
    Box,
    Text,
    VStack,
    HStack,
} from "@chakra-ui/react";
import {FiCheckCircle, FiClock, FiMail, FiXCircle, FiUsers, FiShoppingCart, FiGift, FiSettings} from "react-icons/fi";

const StatsCards = ({stats, loading}) => {
    const cardBg = useColorModeValue("white", "gray.700");
    const spinnerColor = useColorModeValue("blue.500", "blue.300");
    const textColor = useColorModeValue("gray.800", "white");

    // Extract stats data - handle both direct stats and API response format
    const statsData = stats?.data || stats || {};
    const byTypeData = statsData.byType || {};

    // Calculate totals for different categories
    const orderTemplates = (byTypeData.order_confirmation || 0) + 
                          (byTypeData.order_ready || 0) + 
                          (byTypeData.order_cancelled || 0) + 
                          (byTypeData.order_delivered || 0);

    const userTemplates = (byTypeData.user_registration || 0) + 
                         (byTypeData.user_welcome || 0) + 
                         (byTypeData.password_reset || 0) + 
                         (byTypeData.email_verification || 0);

    const merchantTemplates = (byTypeData.merchant_registration || 0) + 
                             (byTypeData.merchant_approval || 0) + 
                             (byTypeData.merchant_rejection || 0);

    const loyaltyTemplates = (byTypeData.loyalty_points_earned || 0) + 
                            (byTypeData.loyalty_points_redeemed || 0);

    if (loading) {
        return (
            <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} mb={6}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <Card key={i} bg={cardBg}>
                        <CardBody>
                            <Spinner size="sm" color={spinnerColor}/>
                        </CardBody>
                    </Card>
                ))}
            </Grid>
        );
    }

    return (
        <Box mb={6}>
            {/* Main Statistics */}
            <Grid 
                templateColumns={{ 
                    base: "repeat(1, 1fr)", 
                    sm: "repeat(2, 1fr)", 
                    md: "repeat(4, 1fr)" 
                }} 
                gap={4} 
                mb={6}
            >
                <Card bg={cardBg}>
                    <CardBody>
                        <Stat>
                            <StatLabel color={textColor}>Total Templates</StatLabel>
                            <StatNumber color={textColor}>{statsData.total || 0}</StatNumber>
                            <StatHelpText>
                                <Icon as={FiMail} mr={1}/>
                                All templates
                            </StatHelpText>
                        </Stat>
                    </CardBody>
                </Card>

                <Card bg={cardBg}>
                    <CardBody>
                        <Stat>
                            <StatLabel color={textColor}>Active Templates</StatLabel>
                            <StatNumber color="green.500">{statsData.active || 0}</StatNumber>
                            <StatHelpText>
                                <Icon as={FiCheckCircle} mr={1}/>
                                Ready to use
                            </StatHelpText>
                        </Stat>
                    </CardBody>
                </Card>

                <Card bg={cardBg}>
                    <CardBody>
                        <Stat>
                            <StatLabel color={textColor}>Draft Templates</StatLabel>
                            <StatNumber color="yellow.500">{statsData.draft || 0}</StatNumber>
                            <StatHelpText>
                                <Icon as={FiClock} mr={1}/>
                                In progress
                            </StatHelpText>
                        </Stat>
                    </CardBody>
                </Card>

                <Card bg={cardBg}>
                    <CardBody>
                        <Stat>
                            <StatLabel color={textColor}>Inactive Templates</StatLabel>
                            <StatNumber color="red.500">{statsData.inactive || 0}</StatNumber>
                            <StatHelpText>
                                <Icon as={FiXCircle} mr={1}/>
                                Disabled
                            </StatHelpText>
                        </Stat>
                    </CardBody>
                </Card>
            </Grid>

            {/* Template Type Statistics */}
            <Grid 
                templateColumns={{ 
                    base: "repeat(1, 1fr)", 
                    sm: "repeat(2, 1fr)", 
                    lg: "repeat(4, 1fr)" 
                }} 
                gap={4}
            >
                <Card bg={cardBg}>
                    <CardBody>
                        <Stat>
                            <StatLabel color={textColor}>Order Templates</StatLabel>
                            <StatNumber color="blue.500">{orderTemplates}</StatNumber>
                            <StatHelpText>
                                <Icon as={FiShoppingCart} mr={1}/>
                                Order-related emails
                            </StatHelpText>
                        </Stat>
                    </CardBody>
                </Card>

                <Card bg={cardBg}>
                    <CardBody>
                        <Stat>
                            <StatLabel color={textColor}>User Templates</StatLabel>
                            <StatNumber color="purple.500">{userTemplates}</StatNumber>
                            <StatHelpText>
                                <Icon as={FiUsers} mr={1}/>
                                User account emails
                            </StatHelpText>
                        </Stat>
                    </CardBody>
                </Card>

                <Card bg={cardBg}>
                    <CardBody>
                        <Stat>
                            <StatLabel color={textColor}>Merchant Templates</StatLabel>
                            <StatNumber color="orange.500">{merchantTemplates}</StatNumber>
                            <StatHelpText>
                                <Icon as={FiSettings} mr={1}/>
                                Merchant management
                            </StatHelpText>
                        </Stat>
                    </CardBody>
                </Card>

                <Card bg={cardBg}>
                    <CardBody>
                        <Stat>
                            <StatLabel color={textColor}>Loyalty Templates</StatLabel>
                            <StatNumber color="pink.500">{loyaltyTemplates}</StatNumber>
                            <StatHelpText>
                                <Icon as={FiGift} mr={1}/>
                                Loyalty program emails
                            </StatHelpText>
                        </Stat>
                    </CardBody>
                </Card>
            </Grid>

            {/* Detailed Breakdown */}
            {Object.keys(byTypeData).length > 0 && (
                <Card bg={cardBg} mt={4}>
                    <CardBody>
                        <Text fontSize="lg" fontWeight="semibold" color={textColor} mb={4}>
                            Template Type Breakdown
                        </Text>
                        <Grid 
                            templateColumns={{ 
                                base: "repeat(1, 1fr)", 
                                sm: "repeat(2, 1fr)", 
                                md: "repeat(3, 1fr)", 
                                lg: "repeat(4, 1fr)" 
                            }} 
                            gap={3}
                        >
                            {Object.entries(byTypeData).map(([type, count]) => {
                                if (count === 0) return null;
                                
                                const typeLabel = type
                                    .split('_')
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ');
                                
                                return (
                                    <Box key={type} p={3} bg="gray.50" borderRadius="md">
                                        <HStack justify="space-between">
                                            <Text fontSize="sm" color={textColor} fontWeight="medium">
                                                {typeLabel}
                                            </Text>
                                            <Text fontSize="sm" color="blue.500" fontWeight="bold">
                                                {count}
                                            </Text>
                                        </HStack>
                                    </Box>
                                );
                            })}
                        </Grid>
                        {Object.values(byTypeData).every(count => count === 0) && (
                            <Text color="gray.500" textAlign="center" py={4}>
                                No templates configured for specific types
                            </Text>
                        )}
                    </CardBody>
                </Card>
            )}
        </Box>
    );
};

export default StatsCards;
